import { z } from 'zod'

const syncStatusSchema = z.enum(['GREEN', 'RED', 'UNKNOWN'])
const nullableTimestamp = z.number().int().positive().nullable()
const dayKeySchema = z.enum(['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'])
const dayRuleSchema = z.union([
  z.object({ status: z.literal('CLOSED') }).strict(),
  z
    .object({ status: z.literal('OPEN'), start: z.string(), end: z.string() })
    .strict(),
])
const intervalsSchema = z.array(
  z.object({ start: z.string(), end: z.string() }).strict()
)
// Readback shape of the canonical optional section; the server validated it.
const ageSchedulingPolicyReadbackSchema = z
  .object({
    enabled: z.boolean(),
    rules: z.array(
      z
        .object({
          minimumYears: z.number().int(),
          maximumYears: z.number().int().optional(),
          windows: z.array(
            z.object({ start: z.string(), end: z.string() }).strict()
          ),
        })
        .strict()
    ),
  })
  .strict()
const weeklyIntervalsSchema = z
  .object({
    sun: intervalsSchema,
    mon: intervalsSchema,
    tue: intervalsSchema,
    wed: intervalsSchema,
    thu: intervalsSchema,
    fri: intervalsSchema,
    sat: intervalsSchema,
  })
  .strict()

const providerResourcesSchema = z
  .object({
    resourceRevision: z.number().int().positive(),
    observationRevision: z.number().int().positive(),
    validatedAtMs: z.number().int().positive(),
    updatedAtMs: z.number().int().positive(),
    freshness: z.enum(['CURRENT', 'STALE']),
    syncHealth: z
      .object({
        read: syncStatusSchema,
        write: syncStatusSchema,
        readStatusAtMs: nullableTimestamp,
        writeStatusAtMs: nullableTimestamp,
      })
      .strict(),
    counts: z
      .object({
        providers: z.number().int().nonnegative(),
        operatories: z.number().int().nonnegative(),
        appointmentTypes: z.number().int().nonnegative(),
        workingHours: z.number().int().nonnegative(),
      })
      .strict(),
    providers: z.array(
      z
        .object({
          displayLabel: z.string().nullable(),
          requestable: z.boolean(),
          inactive: z.boolean(),
        })
        .strict()
    ),
    operatories: z.array(z.object({ name: z.string().min(1) }).strict()),
    appointmentTypes: z.array(
      z
        .object({
          name: z.string().min(1),
          minutes: z.number().int().positive(),
          bookableOnline: z.boolean().nullable(),
          parentType: z.enum(['LOCATION', 'INSTITUTION']).nullable(),
        })
        .strict()
    ),
    pmsWorkingHours: z.array(
      z
        .object({
          days: z.array(z.string()),
          specificDate: z.string().nullable(),
          customRecurrence: z
            .object({
              ref: z.string(),
              unit: z.enum(['daily', 'weekly', 'monthly']),
              num: z.number().int().positive(),
            })
            .strict()
            .nullable(),
          beginTime: z.string(),
          endTime: z.string(),
          active: z.boolean(),
          source: z.string(),
          providerDisplayLabel: z.string().nullable(),
          operatoryName: z.string().nullable(),
          appointmentTypeNames: z.array(z.string()),
        })
        .strict()
    ),
    flioSchedulingPolicy: z
      .object({
        policyRevision: z.number().int().positive(),
        policyFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
        timeZone: z.string().min(1),
        workingHours: z
          .object({
            sun: dayRuleSchema,
            mon: dayRuleSchema,
            tue: dayRuleSchema,
            wed: dayRuleSchema,
            thu: dayRuleSchema,
            fri: dayRuleSchema,
            sat: dayRuleSchema,
          })
          .strict(),
        breaks: z.array(
          z
            .object({
              name: z.string().min(1).max(80).optional(),
              days: z.array(dayKeySchema),
              start: z.string(),
              end: z.string(),
            })
            .strict()
        ),
        specialDays: z.array(
          z.union([
            z
              .object({
                name: z.string().min(1).max(80).optional(),
                date: z.string(),
                status: z.literal('CLOSED'),
              })
              .strict(),
            z
              .object({
                name: z.string().min(1).max(80).optional(),
                date: z.string(),
                status: z.literal('OPEN'),
                start: z.string(),
                end: z.string(),
              })
              .strict(),
          ])
        ),
        ageSchedulingPolicy: ageSchedulingPolicyReadbackSchema.optional(),
      })
      .strict()
      .nullable(),
    flioSchedulingPolicyInitialization: z
      .object({
        expectedPolicyRevision: z.literal(0),
        timeZone: z.string().min(1),
        workingHours: z
          .object({
            sun: dayRuleSchema,
            mon: dayRuleSchema,
            tue: dayRuleSchema,
            wed: dayRuleSchema,
            thu: dayRuleSchema,
            fri: dayRuleSchema,
            sat: dayRuleSchema,
          })
          .strict(),
        breaks: z.array(
          z
            .object({
              name: z.string().min(1).max(80).optional(),
              days: z.array(dayKeySchema),
              start: z.string(),
              end: z.string(),
            })
            .strict()
        ),
        specialDays: z.array(
          z.union([
            z
              .object({
                name: z.string().min(1).max(80).optional(),
                date: z.string(),
                status: z.literal('CLOSED'),
              })
              .strict(),
            z
              .object({
                name: z.string().min(1).max(80).optional(),
                date: z.string(),
                status: z.literal('OPEN'),
                start: z.string(),
                end: z.string(),
              })
              .strict(),
          ])
        ),
      })
      .strict()
      .nullable(),
    effectiveSchedulingHours: z
      .object({
        schemaVersion: z.literal(1),
        tenantUid: z.string().min(1),
        timeZone: z.string().min(1),
        policyRevision: z.number().int().positive(),
        policyFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
        resourceRevision: z.number().int().positive(),
        resourceFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
        pmsWorkingHoursObservedAtMs: z.number().int().positive(),
        evaluatedAtMs: z.number().int().positive(),
        weekly: weeklyIntervalsSchema,
        dateOverrides: z.record(z.string(), intervalsSchema),
        effectiveHoursFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
      })
      .strict()
      .nullable(),
  })
  .strict()

export const providerResourceSettingsSchema = z
  .object({
    schemaVersion: z.literal(1),
    authorityMode: z.enum([
      'TENANT_SECRET',
      'PROVIDER_ACCOUNT_V1',
      'FLIO_INFORMATIONAL',
    ]),
    management: z.enum([
      'LEGACY_FLIO_POLICY',
      'PMS_READ_ONLY',
      'FLIO_CLINIC_HOURS',
    ]),
    status: z.enum([
      'LEGACY',
      'AUTHORITY_PENDING',
      'REFRESH_REQUIRED',
      'AVAILABLE',
    ]),
    providerResources: providerResourcesSchema.nullable(),
  })
  .strict()

export type ProviderResourceSettings = z.infer<
  typeof providerResourceSettingsSchema
>

const patientTypePolicySchema = z.enum(['NEW', 'EXISTING', 'BOTH'])
const patientAgePolicySchema = z
  .object({
    minimumYears: z.number().int().min(0).max(130),
    maximumYears: z.number().int().min(0).max(130),
  })
  .strict()
  .refine((policy) => policy.minimumYears <= policy.maximumYears, {
    message: 'Minimum age must not exceed maximum age',
    path: ['maximumYears'],
  })

const bookingServiceSchema = z
  .object({
    informationalServiceId: z.string().min(1).max(128).optional(),
    serviceIntent: z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/),
    patientFacingIntent: z
      .string()
      .regex(/^[A-Z][A-Z0-9_]{0,63}$/)
      .optional(),
    serviceLabel: z.string().min(1).max(120),
    appointmentTypeId: z.number().int().positive(),
    patientTypePolicy: patientTypePolicySchema,
    patientAgePolicy: patientAgePolicySchema.optional(),
    // The clinic's one Emergency service (a service or every variant of one
    // intent group). Only true is stored; unmarked services omit the field.
    emergencyService: z.literal(true).optional(),
  })
  .strict()

export const serviceBookingSettingsSchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.enum([
      'AUTHORITY_UNAVAILABLE',
      'RESOURCE_REFRESH_REQUIRED',
      'REVIEW_REQUIRED',
      'AVAILABLE',
    ]),
    reviewRequiredReason: z.string().min(1).nullable().optional(),
    bookingReadiness: z
      .object({
        decision: z.enum(['READY', 'PROVIDER_GATED']),
        decisionReason: z.enum([
          'SYNC_HEALTHY',
          'LIVE_PROVIDER_CAPABILITY_REQUIRED',
        ]),
        readinessRevision: z.number().int().positive(),
        evaluatedAtMs: z.number().int().positive(),
        expiresAtMs: z.number().int().positive(),
      })
      .strict()
      .nullable()
      .optional(),
    configuration: z
      .object({
        resourceRevision: z.number().int().positive(),
        mappingRevision: z.number().int().nonnegative(),
        mappingFingerprint: z.union([
          z.literal('MISSING'),
          z.string().regex(/^[a-f0-9]{64}$/),
        ]),
        services: z.array(bookingServiceSchema).max(100),
        appointmentTypes: z.array(
          z
            .object({
              id: z.number().int().positive(),
              name: z.string().min(1),
              minutes: z.number().int().positive(),
              bookableOnline: z.boolean().nullable(),
              parentType: z.enum(['LOCATION', 'INSTITUTION']).nullable(),
              eligible: z.boolean(),
              providerEvidence: z.array(
                z
                  .object({
                    id: z.number().int().positive(),
                    displayLabel: z.string().nullable(),
                    requestable: z.boolean(),
                  })
                  .strict()
              ),
              operatoryEvidence: z.array(
                z
                  .object({
                    id: z.number().int().positive(),
                    name: z.string().min(1),
                  })
                  .strict()
              ),
              associationCount: z.number().int().nonnegative(),
              patientTypePolicy: patientTypePolicySchema.nullable(),
            })
            .strict()
        ),
      })
      .strict()
      .nullable(),
  })
  .strict()

export const serviceBookingUpdateSchema = z
  .object({
    expectedMappingRevision: z.number().int().nonnegative(),
    expectedMappingFingerprint: z.union([
      z.literal('MISSING'),
      z.string().regex(/^[a-f0-9]{64}$/),
    ]),
    services: z.array(bookingServiceSchema).max(100),
  })
  .strict()

export type ServiceBookingSettings = z.infer<
  typeof serviceBookingSettingsSchema
>
export type ServiceBookingUpdate = z.infer<typeof serviceBookingUpdateSchema>
export type BookingService = z.infer<typeof bookingServiceSchema>
